import useNovelNovemberSprint from "../../../hooks/useNovelNovemberSprint";
import useWriteModel from "../../../hooks/useWriteModel";
import { ResizeObserver } from "@juggle/resize-observer";
import { resolveBenchmarkWriteModel } from "../../BenchmarkTakeover/proseMode";
import classnames from "classnames";
import { keyBy } from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import update from "react-addons-update";
import * as ReactDOM from "react-dom";
import SVG from "react-inlinesvg";
import { useHistory } from "react-router-dom";
import useMeasure from "react-use-measure";
import { AppEventPublisher } from "src/AppEventPublisher";
import { useStableCallback } from "src/hooks/useStableCallback";
import Auth from "../../../Auth.js";

import { MAX_VISUALIZE_WORDS, MIN_VISUALIZE_WORDS, UNLIMITED_EXPERIMENT_VARIATIONS, WRITE_MODE_GUIDED } from "../../../../../src/shared/constants.js";
import { BLOCK_TYPES } from "../../../../../src/shared/schemas/PluginSchema.js";
import useInstalledPlugins from "../../../hooks/useInstalledPlugins.js";
import useUserCustomization from "../../../hooks/useUserCustomization.js";
import SettingsPanel from "../../../SettingsPanel.js";
import { useAppStore } from "../../../stores/appStore.js";
import { useProjectStore } from "../../../stores/projectStore.js";
import { useStoryBibleStore } from "../../../stores/storyBibleStore.js";
import { useSudowriteStore } from "../../../stores/sudowriteStore.js";
import { useOnboardingStore } from "../../../stores/onboardingStore";
import { SubscribeButton, makeId } from "../../../Utils.js";
import { useAllFoldersStore } from "../../AppHome/store.js";
import { Button } from "../../Button/index.js";

import useMyVoiceSelectedSample from "../../../hooks/useMyVoiceSelectedSample.js";
import { SudoTooltip } from "../../../sudokit/components/SudoTooltip/index.js";
import { CustomizePlugins } from "../../CustomizePlugins";
import DescribeMenu from "../../DescribeMenu/index.js";
import DocumentSharingMenu from "../../DocumentSharingMenu/index.js";
import FeaturebaseChangelog from "../../FeaturebaseChangelog/index.js";
import Guidances from "../../Guidances/index.js";
import HelpMenu from "../../HelpMenu/index.js";
import { usePluginPopover } from "../../Modals/UserTextInputPluginForm/index.js";
import RewriteMenu from "../../RewriteMenu/index.js";
import Status from "../Status/index.js";
import Item from "./Item/index.js";
import MoreMenu from "./MoreMenu/index.js";
import { TopMenuContainer } from "./styles.js";
import WriteButton from "./WriteButton/index.js";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor.js";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { AnchorIds, TourIds, TourSignal } from "../../StickyTour/TourTypes";
import ExitTourButton from "../../StickyTour/ExitTourButton.js";
import { CrossAppSignal, useCrossAppSignalStore, getPluginSignal } from "../../../stores/crossAppSignalStore";
import { Spinner } from "src/sudokit/components/Spinner";
import { FeedbackTabLabel } from "../EditorToolbar/FeedbackTabLabel";
import { useFeedbackToggle } from "../../../hooks/useFeedbackToggle";
import { FeedbackTypeSelector } from "../FeedbackTypeSelector";
import { FeedbackCreditWarning } from "../FeedbackCreditWarning";
import FeedbackPlanUpsellDialog from "../FeedbackPlanUpsellDialog";
import {
  shouldRequireFeedbackCategorySelection,
  shouldShowFeedbackCategoryLabel,
} from "../feedbackCategorySelection";
import {
  normalizeFeedbackPreferences,
  setFeedbackPreferenceCategory,
} from "../feedbackProjectPreferences";


/**
 * This will reconcile the menu order with the user's updated list of installed plugins
 * and our list of default tools.
 * @param {*} installedPlugins
 * @param {*} customOrder
 * @param {*} defaultToolMap
 * @returns
 */
const reconcileReorderableMenu = (installedPlugins, customOrder, defaultToolMap) => {
  const safeInstalledPlugins = Array.isArray(installedPlugins) ? installedPlugins : [];
  const safeCustomOrder = Array.isArray(customOrder) ? customOrder : [];
  const installedCustomPlugins = safeInstalledPlugins.filter((plugin) => showPlugin(plugin.plugin));
  const availableDefaultIds = [...DEFAULT_TOOLBAR_IDS, ...DEFAULT_PLUGINS_MENU_IDS].filter((id) => defaultToolMap?.[id]?.shouldShow?.());

  const lookup = keyBy(installedCustomPlugins, "plugin.id");
  const defaultLookup = availableDefaultIds.reduce((map, id) => {
    map[id] = true;
    return map;
  }, {});
  const mergedLookup = { ...defaultLookup, ...lookup };

  let ordered = [];

  for (const id of safeCustomOrder) {
    const plugin = mergedLookup[id];
    if (plugin) {
      ordered.push(id);

      delete mergedLookup[id];
    }
  }

  // Insert feedback at its intended position (3rd button) when newly available,
  // rather than appending at the end where it would be buried in More Tools
  const feedbackIdx = ordered.indexOf("feedback");
  if (feedbackIdx === -1 && mergedLookup["feedback"]) {
    ordered.splice(2, 0, "feedback");
    delete mergedLookup["feedback"];
  }
  ordered = ordered.concat(Object.keys(mergedLookup));
  return ordered;
};

/**
 * Return true if custom user plugin. (False for all the dynamic Write plugins + other built-ins)
 */
const showPlugin = (plugin) => {
  if (!plugin || !["document", "story-bible"].includes(plugin.type)) return false;
  const inputDocumentBlock = plugin.blocks?.find((block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT);
  if (!inputDocumentBlock) return false;

  return true;
};

const getHideBreakpoints = (wordCountVisible, adminButtonVisible, highlightedWords) => {
  const extraVisibleCount = [wordCountVisible, adminButtonVisible, highlightedWords].filter(Boolean).length;

  if (extraVisibleCount === 0) {
    return {
      back: 1000,
      labs: 1120,
      admin: 1250,
    };
  }

  if (extraVisibleCount === 1) {
    return {
      admin: 1250,
      back: 1000,
      labs: 1210,
    };
  }

  return {
    admin: 1250,
    back: 1000,
    labs: 1200,
  };
};

const TOP_BUTTON_WIDTH = 200; // to do: make this dynamic
const TOOLBAR_TOP_BUTTON_WIDTH = TOP_BUTTON_WIDTH;
const MORE_TOOLS_RESERVED_WIDTH = 170;
const MORE_TOOLS_OPEN_WIDTH = 209;
const MORE_TOOLS_OPEN_EXPANSION = MORE_TOOLS_OPEN_WIDTH - MORE_TOOLS_RESERVED_WIDTH;
const TOOLBAR_HORIZONTAL_MARGIN = 32;
const TOOL_GAP = 0;
const TOOL_WIDTHS = {
  write: 145,
  rewrite: 160,
  describe: 180,
  feedback: 220,
  brainstorm: 145,
  visualize: 145,
  firstDraft: 155,
  canvas: 130,
  admin: 120,
};
const DEFAULT_TOOLBAR_IDS = ["write", "rewrite", "describe", "feedback", "brainstorm", "admin"];
const DEFAULT_PLUGINS_MENU_IDS = ["visualize", "firstDraft", "canvas", "reviser", "old-feedback"];
const DEFAULT_NUMBER_OF_TOOLS_TO_SHOW = 4;
const MIN_NUMBER_OF_TOOLS_TO_SHOW = 3;
const TOOLBAR_OVERFLOW_TOLERANCE = 1;

const getToolbarRect = () => {
  return document.querySelector(".project-menu")?.getBoundingClientRect();
};

const getNumberOfToolsToShow = (orderedTools, toolbarWidth) => {
  const availableLength = toolbarWidth - TOOLBAR_HORIZONTAL_MARGIN - MORE_TOOLS_RESERVED_WIDTH - MORE_TOOLS_OPEN_EXPANSION;
  let usedLength = 0;
  let numberOfToolsToShow = 0;

  for (const toolId of orderedTools) {
    const toolWidth = (TOOL_WIDTHS[toolId] || TOOLBAR_TOP_BUTTON_WIDTH) + TOOL_GAP;
    if (usedLength + toolWidth > availableLength + TOOLBAR_OVERFLOW_TOLERANCE) {
      break;
    }

    usedLength += toolWidth;
    numberOfToolsToShow += 1;
  }

  return Math.min(orderedTools.length, numberOfToolsToShow);
};

const getAvailableLengthForButtonMenu = () => {
  const viewportWidth = window.innerWidth;
  const backbutton = document.querySelector(".back-button");
  const backButtonWidth = backbutton?.getBoundingClientRect().width || 0;
  const statusButton = document.querySelector(".status");
  const statusButtonWidth = statusButton?.getBoundingClientRect().width || 0;
  const settingsButton = document.querySelector("#settings-container");
  const settingsButtonWidth = settingsButton?.getBoundingClientRect().width || 0;
  const helpContainer = document.querySelector(".toolbar-help-container");
  const helpContainerWidth = helpContainer?.getBoundingClientRect().width || 0;
  const documentSharingButton = document.querySelector(".toolbar-documentsharing");
  const documentSharingButtonWidth = documentSharingButton?.getBoundingClientRect().width || 0;
  const membershipStatus = document.querySelector(".membership-status");
  const membershipStatusWidth = membershipStatus?.getBoundingClientRect().width || 0;

  const totalMenuWidth = backButtonWidth + statusButtonWidth + settingsButtonWidth + helpContainerWidth + documentSharingButtonWidth + membershipStatusWidth;
  const availableLength = viewportWidth - totalMenuWidth - 220; // 220 is the width of the plugins drop down menu when open
  return availableLength;
};
const Menu = React.forwardRef(
  (
    {
      project,
      menuRef,
      describeSenses,
      setDescribeSenses,
      writeMode,
      writeOpen,
      setWriteOpen,
      setDescribeOpen,
      setWriteMode,
      guidanceOpen,
      loadingGuidances,
      closeGuidance,
      openGuidance,
      setToneOpen,
      modalOpen,
      setModalOpen,
      setTone,
      tones,
      tone,

      labsOpen,
      openDescription,
      openFilter,
      openBrainstorm,
      openFeedback,
      setStoryToolOpen,
      openWormhole,
      setLabsOpen,
      hasAccessToFeatures,
      documentId,
      fetchGuidance,
      guidances,
      automaticGuidances,
      setAutomaticGuidances,
      setGuidances,
      memory,
      setMemory,
      memoryEnabled,
      setMemoryEnabled,

      wordCount,
      highlightedWordCount,
      editorOptions,
      saved,
      openVisualize,

      wormholeOutputLength, //
      wormholeOutputCards,
      creativitySliderPosition,
      setEditorOptions,

      openAdmin,
      runDocumentPluginAction,
      documentPluginBridge,
      hideHiring = true,
      hideNew = false,
      hideHelp = false,
      statusText = null,
      showFront,
      openAgenticChatFromHelp,

      setCommandModeState,
      showFirstDraft,
      writeModel,
      trashedItem,
      hasDocument: hasDocumentProp,
      hideBackButton = false,
      hideSettings = false,
      minimal = false,
    },
    ref
  ) => {
    const hasDocument = typeof hasDocumentProp === "boolean" ? hasDocumentProp : Boolean(documentId);
    // SW-7957: When SW2 is on, the ProjectTopBar owns back-button + settings + help + share + status.
    // Suppress them here to avoid duplicates in the action toolbar.
    const isSW2 = useAppStore((state) => state.isSW2);
    const handledByTopBar = isSW2;
    const userData = useSudowriteStore((state) => state.userData);
    const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
    const settingsOpen = useSudowriteStore((state) => state.settingsOpen);
    const setSettingsOpen = useSudowriteStore((state) => state.setSettingsOpen);
    const demo = useSudowriteStore((state) => state.demo);
    const setDemo = useSudowriteStore((state) => state.setDemo);
    const helpOpen = useSudowriteStore((state) => state.helpOpen);
    const setHelpOpen = useSudowriteStore((state) => state.setHelpOpen);
    const subscription = useSudowriteStore((state) => state.subscription);
    const columnWidths = useProjectStore((state) => state.columnWidths);
    const setProseGeneratorActive = useStoryBibleStore((state) => state.setProseGeneratorActive);
    const metadata = useStoryBibleStore((state) => state.metadata);
    const selectedWriteModel = useWriteModel({ editorOptions, initialWriteModel: writeModel });
    const {
      chapterEditorActive,
      selectedFeedbackCategory,
      hasUserSelectedFeedbackCategory,
      handleFeedbackClick,
      handleChevronClick: handleFeedbackChevronClickBase,
      showCreditWarning,
      confirmFeedback,
      dismissCreditWarning,
      estimatedCredits,
      isEstimatingFeedback,
    } = useFeedbackToggle(project, documentId);
    const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
    const sendFailureSignal = useStickyTourStore((state) => state.sendFailureSignal);

    const abortFeedback = useStoryBibleStore((state) => state.abortFeedback);
    const handleFeedbackStop = useStableCallback(() => {
      abortFeedback();
      sendFailureSignal(TourSignal.FEEDBACK_CANCELLED);
    });
    const setSelectedFeedbackCategory = useStoryBibleStore((state) => state.setSelectedFeedbackCategory);
    const setMetadata = useStoryBibleStore((state) => state.setMetadata);
    const collapsed = hasDocument && columnWidths.center.collapsed;
    const history = useHistory();
    const isTourActive = useStickyTourStore((state) => state.isTourActive);
    const activeTourId = useStickyTourStore((state) => state.activeTourId);
    const steps = useStickyTourStore((state) => state.steps);
    const currentStepIndex = useStickyTourStore((state) => state.currentStepIndex);
    const activeStepId = steps[currentStepIndex]?.id;
    const generatedPlugin = useOnboardingStore((state) => state.generatedPlugin);

    const isOnStepWithHiddenExitButton =
      (activeTourId === TourIds.WRITE_REWRITE && activeStepId === "8") ||
      (activeTourId === TourIds.STORY_BIBLE && activeStepId === "1");
    const isFeedbackMainActionDisabled = activeTourId === TourIds.FEEDBACK && (activeStepId === "4" || activeStepId === "5");
    const shouldShowExitTourButton =
      isTourActive &&
      activeTourId !== TourIds.FIND_TOUR_AGAIN &&
      activeTourId !== TourIds.CHAT_ONBOARDING &&
      !isOnStepWithHiddenExitButton;
    const shouldShowLegacyExitTourButton = shouldShowExitTourButton && !handledByTopBar;

    const { getProjectFromFirebaseId, isInTrash } = useAllFoldersStore((state) => ({
      getProjectFromFirebaseId: state.getProjectFromFirebaseId,
      isInTrash: state.isInTrash,
    }));
    const HIDE_BREAKPOINTS = getHideBreakpoints(editorOptions.showWordCount, openAdmin, highlightedWordCount);

    const pluginPopover = usePluginPopover();

    const { installedPlugins: pluginSchemas, isLoading: installedPluginsLoading } = useInstalledPlugins();
    const { customization, isLoading: customizationLoading, refetch: refetchUserCustomization } = useUserCustomization();
    const isLoading = installedPluginsLoading || customizationLoading;

    // Unlimited Sudowrite Experiment
    const isInUnlimitedExperiment = useSudowriteStore((state) => state.isInUnlimitedExperiment);
    const unlimitedExperimentVariation = useSudowriteStore((state) => state.unlimitedExperimentVariation);
    const [showTryUnlimitedTooltip, setShowTryUnlimitedTooltip] = useState(false);
    const [showTryMuseTooltip, setShowTryMuseTooltip] = useState(false);
    const [usedWriteCount, setUsedWriteCount] = useState(0);
    const [isCustomizePluginsModalOpen, setIsCustomizePluginsModalOpen] = useState(false);
    const [feedbackTypeSelectorOpen, setFeedbackTypeSelectorOpen] = useState(false);
    const isFeedbackBetaUser = subscription?.isFeedbackBetaUser === true;
    const [feedbackPlanUpsellOpen, setFeedbackPlanUpsellOpen] = useState(false);
    const [feedbackPlanUpsellSource, setFeedbackPlanUpsellSource] = useState("toolbar_button");
    const getFeedbackUpsellCurrentPlanTitle = () => {
      if (subscriptionJSON?.currentPlan?.title) return subscriptionJSON.currentPlan.title;
      if (subscriptionJSON?.planName) return subscriptionJSON.planName;
      if (typeof subscription?.currentPlan === "function") {
        return subscription.currentPlan()?.title || null;
      }
      return null;
    };
    const openFeedbackPlanUpsell = (source) => {
      const props = {
        feedbackUpsellSource: source,
        currentPlanTitle: getFeedbackUpsellCurrentPlanTitle(),
      };
      AppEventPublisher.feedback.planUpsellTriggered(props);
      setFeedbackPlanUpsellSource(source);
      setFeedbackPlanUpsellOpen(true);
    };

    const shouldOpenFeedbackTypeSelectorBeforeRun = shouldRequireFeedbackCategorySelection({
      selectedFeedbackCategory,
      hasUserSelectedFeedbackCategory,
    });
    const showFeedbackCategoryLabel = shouldShowFeedbackCategoryLabel({
      selectedFeedbackCategory,
      hasUserSelectedFeedbackCategory,
    });

    const openFeedbackTypeSelector = () => {
      handleFeedbackChevronClickBase();
      setFeedbackTypeSelectorOpen(true);
      sendFlowSignal(TourSignal.FEEDBACK_CHEVRON_CLICKED);
    };

    const persistFeedbackCategorySelection = (categoryKey) => {
      const currentPreferences = normalizeFeedbackPreferences(project?.data?.feedbackPreferences);
      const nextPreferences = setFeedbackPreferenceCategory(
        currentPreferences,
        categoryKey,
      );

      setSelectedFeedbackCategory(categoryKey, project?.id);
      if (project?.update) {
        project.update({
          feedbackPreferences: nextPreferences,
        });
      }
    };

    const handleFeedbackClickWithClose = useStableCallback((e) => {
      if (shouldOpenFeedbackTypeSelectorBeforeRun) {
        openFeedbackTypeSelector();
        return;
      }
      setFeedbackTypeSelectorOpen(false);
      const paidTierUnlock = !!(subscription?.hasPaidTierManuscriptFeedbackAccess);
      if (!paidTierUnlock) {
        openFeedbackPlanUpsell("toolbar_button");
        return;
      }
      // Keep the overflow entry visible while its quote is pending.
      if (!chapterEditorActive) e?.stopPropagation();
      handleFeedbackClick();
    });
    const handleFeedbackChevronClick = useStableCallback(() => {
      const paidTierUnlock = !!(subscription?.hasPaidTierManuscriptFeedbackAccess);
      if (!paidTierUnlock) {
        openFeedbackPlanUpsell("toolbar_chevron");
        return;
      }
      handleFeedbackChevronClickBase();
      setFeedbackTypeSelectorOpen((prev) => {
        const next = !prev;
        if (next) {
          sendFlowSignal(TourSignal.FEEDBACK_CHEVRON_CLICKED);
        }
        return next;
      });
    });

    const handleFeedbackTypeSelectorClose = useStableCallback(() => {
      setFeedbackTypeSelectorOpen(false);
    });

    const handleFeedbackCategorySelect = useStableCallback((categoryKey) => {
      persistFeedbackCategorySelection(categoryKey);
      sendFlowSignal(TourSignal.FEEDBACK_TYPE_SELECTED);
    });

    // Reorderable Menu
    const [orderedTools, _setOrderedTools] = useState([]);
    const setOrderedTools = (newOrder) => {
      _setOrderedTools(newOrder);
      splitToolsIntoMainAndMore(newOrder);
    };
    const [mainMenuTools, setMainMenuTools] = useState([]);
    const [moreMenuTools, setMoreMenuTools] = useState([]);
    const [menuInitialized, setMenuInitialized] = useState(false);
    const [pluginSchemasMap, setPluginSchemasMap] = useState({});
    const mainMenuRef = useRef(null);
    const sw2ToolbarHasSettledRef = useRef(false);
    const [sw2ToolbarSettled, setSw2ToolbarSettled] = useState(false);

    const getVisualizedText = useMemo(() => {
      if (!highlightedWordCount || highlightedWordCount < MIN_VISUALIZE_WORDS) {
        return `Visualize your story with AI-generated images. Works with between ${MIN_VISUALIZE_WORDS}-${MAX_VISUALIZE_WORDS} words.`;
      } else if (!!highlightedWordCount && highlightedWordCount > MAX_VISUALIZE_WORDS) {
        return `Visualize works on up to ${MAX_VISUALIZE_WORDS} words.  You have ${highlightedWordCount} words selected.`;
      } else if (!!highlightedWordCount && highlightedWordCount >= MIN_VISUALIZE_WORDS && highlightedWordCount <= MAX_VISUALIZE_WORDS) {
        return "Click to visualize your story with AI-generated images.";
      }
      return "Visualize your story with AI-generated images.";
    }, [highlightedWordCount]);

    const incrementUsedWriteCount = () => {
      if (isInUnlimitedExperiment && unlimitedExperimentVariation !== "control") {
        // set a cookie in local storage for how many times they've used write:
        const cookieName = `unlimited-sudowrite-experiment-used-write`;
        const cookieValue = parseInt(localStorage.getItem(cookieName), 10) || 0;
        localStorage.setItem(cookieName, cookieValue + 1);
        setUsedWriteCount(cookieValue + 1);
      }
    };

    const { selectedSample } = useMyVoiceSelectedSample();
    const myVoiceDisplayName = selectedSample?.name || "Your Import";

    const isRewriteRunning = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.REWRITE_RUNNING]);
    const isDescribeRunning = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.DESCRIBE_RUNNING]);
    const isFeedbackRunning = useCrossAppSignalStore((state) => state.signals[CrossAppSignal.FEEDBACK_RUNNING]);
    const crossAppSignals = useCrossAppSignalStore((state) => state.signals);
    const wasFeedbackRunningRef = useRef(false);

    useEffect(() => {
      const feedbackTourWaitingForStart = activeTourId === TourIds.FEEDBACK && activeStepId === "6";
      if (feedbackTourWaitingForStart && isFeedbackRunning && !wasFeedbackRunningRef.current) {
        sendFlowSignal(TourSignal.FEEDBACK_BUTTON_CLICKED);
      }
      wasFeedbackRunningRef.current = isFeedbackRunning;
    }, [activeStepId, activeTourId, isFeedbackRunning, sendFlowSignal]);

    const completeMyVoiceOnboarding = useCallback(async () => {
      // Set the dismissing flag immediately to prevent flicker during state updates
      if (window.__myVoiceOnboardingDismissingRef) {
        window.__myVoiceOnboardingDismissingRef.current = true;
      }

      // Clear from both userData and editorOptions
      const updates = {
        showMyVoiceWriteOnboarding: false,
      };

      // Set myVoiceOnboardedAt if not already set (so it doesn't show in other projects)
      if (!userData?.data?.myVoiceOnboardedAt) {
        updates.myVoiceOnboardedAt = new Date().toISOString();
      }

      if (userData?.data?.showMyVoiceWriteOnboarding || !userData?.data?.myVoiceOnboardedAt) {
        await userData.update(updates);
      }

      if (editorOptions?.showMyVoiceWriteOnboarding) {
        setEditorOptions({ ...editorOptions, showMyVoiceWriteOnboarding: false });
      }

      // Clear the hasMyVoiceTraining flag from the project
      // This must be awaited to prevent race condition with useEffect
      if (project?.hasMyVoiceTraining) {
        await project.update({ hasMyVoiceTraining: false });
      }
    }, [userData, editorOptions, setEditorOptions, project]);

    useEffect(() => {
      if (usedWriteCount > 3 && userData && !userData?.data.hideUnlimitedExperimentWriteTooltip) {
        if (isInUnlimitedExperiment && unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.UNLIMITED_DEFAULT) {
          setTimeout(() => {
            setShowTryMuseTooltip(true);
          }, 2000);
        } else if (isInUnlimitedExperiment && unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.MUSE_DEFAULT) {
          setTimeout(() => {
            setShowTryUnlimitedTooltip(true);
          }, 2000);
        }
      }
    }, [usedWriteCount, userData]);

    const splitToolsIntoMainAndMore = (orderedTools) => {
      const safePluginSchemas = Array.isArray(pluginSchemas) ? pluginSchemas : [];
      const installedCustomPlugins = safePluginSchemas.filter((plugin) => showPlugin(plugin.plugin));
      let numberOfToolsToShow = 0;
      const availableLength = getAvailableLengthForButtonMenu();
      const { activeTourId: currentTourId, phase: currentPhase } = useStickyTourStore.getState();
      const pluginsTourActive = currentTourId === TourIds.PLUGINS_QUICKSTART && currentPhase === 'active';
      const feedbackTourActive = currentTourId === TourIds.FEEDBACK && currentPhase === "active";

      if (feedbackTourActive || pluginsTourActive) {
        numberOfToolsToShow = DEFAULT_NUMBER_OF_TOOLS_TO_SHOW;
      } else if (isSW2) {
        const toolbarWidth = getToolbarRect()?.width || window.innerWidth;
        numberOfToolsToShow = getNumberOfToolsToShow(orderedTools, toolbarWidth);
      } else if (!installedCustomPlugins || installedCustomPlugins.length === 0 || pluginsTourActive) {
        if (availableLength < 535) {
          numberOfToolsToShow = MIN_NUMBER_OF_TOOLS_TO_SHOW;
        } else {
          numberOfToolsToShow = DEFAULT_NUMBER_OF_TOOLS_TO_SHOW;
        }
      } else {
        numberOfToolsToShow = Math.floor(availableLength / TOP_BUTTON_WIDTH);
      }
      numberOfToolsToShow = Math.min(orderedTools.length, numberOfToolsToShow);
      const newMainMenuTools = orderedTools.slice(0, numberOfToolsToShow);
      const newMoreMenuTools = orderedTools.slice(numberOfToolsToShow);
      if (isSW2 && !sw2ToolbarHasSettledRef.current) {
        setSw2ToolbarSettled(false);
      }
      setMainMenuTools(newMainMenuTools);
      setMoreMenuTools(newMoreMenuTools);
      setMenuInitialized(true);
    };

    const initializeReorderableMenu = async (pluginSchemas, orderedCustomPlugins, defaultToolMap) => {
      const safePluginSchemas = Array.isArray(pluginSchemas) ? pluginSchemas : [];
      const safeOrderedCustomPlugins = Array.isArray(orderedCustomPlugins) ? orderedCustomPlugins : [];
      const installedCustomPlugins = safePluginSchemas.filter((plugin) => showPlugin(plugin.plugin));
      const unOrderedCustomPlugins = installedCustomPlugins.filter((plugin) => !safeOrderedCustomPlugins.includes(plugin.plugin.id));
      const availableDefaultToolbarIds = DEFAULT_TOOLBAR_IDS.filter((id) => defaultToolMap?.[id]?.shouldShow?.());
      const availableDefaultPluginsMenuIds = DEFAULT_PLUGINS_MENU_IDS.filter((id) => defaultToolMap?.[id]?.shouldShow?.());
      const newOrder = [...availableDefaultToolbarIds, ...safeOrderedCustomPlugins, ...unOrderedCustomPlugins.map((plugin) => plugin.plugin.id), ...availableDefaultPluginsMenuIds];

      await Auth.fetchWithIdToken("/api/user-customization/save-plugin-order", {
        method: "PUT",
        pluginOrder: newOrder,
      });

      setOrderedTools(newOrder);
      splitToolsIntoMainAndMore(newOrder);
    };

    const makePluginHandler = (pluginId, pluginSchemasMap) => {
      const plugin = pluginSchemasMap[pluginId]?.plugin;
      if (!plugin) return;
      const inputDocumentBlock = plugin.blocks.find((block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT);
      const pluginSignal = getPluginSignal(pluginId);
      let handler = () => runDocumentPluginAction(inputDocumentBlock, plugin, documentPluginBridge, false, {}, undefined, pluginSignal);

      const userTextInputOption = inputDocumentBlock.options.outputs.find((option) => option.source === "user_text_input");
      if (userTextInputOption) {
        handler = (targetRef) => {
          if (labsOpen) setLabsOpen(false);
          pluginPopover.activate(
            {
              name: plugin.name,
              instructions: userTextInputOption.instructionsText,
              minWords: userTextInputOption.minWords,
              maxWords: userTextInputOption.maxWords,
              onSubmit(user_text_input) {
                runDocumentPluginAction(inputDocumentBlock, plugin, documentPluginBridge, false, { user_text_input }, undefined, pluginSignal);
              },
            },
            targetRef
          );
        };
      }
      return handler;
    };

    const isGeneratedPlugin = (plugin) => {
      if (activeTourId !== TourIds.PLUGINS_QUICKSTART || !plugin) return false;
      if (!generatedPlugin) return showPlugin(plugin);

      return (
        plugin.shortId === generatedPlugin.shortId ||
        plugin.slug === generatedPlugin.slug ||
        plugin.name === generatedPlugin.name
      );
    };

    useEffect(() => {
      if (!hasDocument) {
        setOrderedTools([]);
        setMainMenuTools([]);
        setMoreMenuTools([]);
        setMenuInitialized(false);
        return;
      }
      if (!userData?.data || isLoading) return;
      const orderedCustomPlugins = customization?.pluginOrder || [];
      if (!Array.isArray(pluginSchemas)) {
        const fallbackOrder = reconcileReorderableMenu([], orderedCustomPlugins, DefaultToolsMap);
        setOrderedTools(fallbackOrder);
        return;
      }
      if (!userData.data.hasReorderableMenu) {
        initializeReorderableMenu(pluginSchemas, orderedCustomPlugins, DefaultToolsMap).catch(() => {
          const fallbackOrder = reconcileReorderableMenu(pluginSchemas, orderedCustomPlugins, DefaultToolsMap);
          setOrderedTools(fallbackOrder);
          splitToolsIntoMainAndMore(fallbackOrder);
        });
        userData.update({ hasReorderableMenu: true });
      } else {
        let pluginOrder = orderedCustomPlugins;
        // One-time migration: rename legacy "feedback" key to "old-feedback" in saved order.
        // Uses a Firestore flag so it only runs once and survives toolbar reorders.
        if (!userData.data.feedbackToolbarMigrated && pluginOrder.includes("feedback")) {
          pluginOrder = pluginOrder.map((id) => (id === "feedback" ? "old-feedback" : id));
          (async () => {
            try {
              await Auth.fetchWithIdToken("/api/user-customization/save-plugin-order", {
                method: "PUT",
                pluginOrder,
              });
              userData.update({ feedbackToolbarMigrated: true });
            } catch {}
          })();
        }
        const newOrder = reconcileReorderableMenu(pluginSchemas, pluginOrder, DefaultToolsMap);
        setOrderedTools(newOrder);
        splitToolsIntoMainAndMore(newOrder);
      }
    }, [userData, userData?.data, pluginSchemas, customization, hasDocument, isLoading]);

    useEffect(() => {
      if (!hasDocument) return;
      const handleResize = () => {
        if (orderedTools.length > 0) {
          splitToolsIntoMainAndMore(orderedTools);
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [orderedTools, hasDocument]);

    useEffect(() => {
      if (pluginSchemas) {
        setPluginSchemasMap(keyBy(pluginSchemas, "plugin.id"));
      }
    }, [pluginSchemas]);

    const handleCustomizePluginsDone = () => {
      refetchUserCustomization();
      setIsCustomizePluginsModalOpen(false);
    };

    const pluginOpenWormhole = useStableCallback(({ guidance = "", sessionId = "" } = {}, nonce) => {
      const pluginWriteMode = writeMode.value === "guided" ? "guided" : "auto"; // Use auto for tone mode

      incrementUsedWriteCount(); // Temp Unlimited Sudowrite Experiment

      const writePlugins = pluginSchemas
        .map((s) => {
          const [, type, mode, model] = s.plugin.id.split("/"); // 'documentPlugins/writeClaude3/auto/claude3haiku'
          return {
            plugin: s.plugin,
            mode,
            model,
            type,
          };
        })
        .filter((s) => (s.type === "write" || s.type === "writeClaude3") && s.mode === pluginWriteMode && s.model === resolveBenchmarkWriteModel(selectedWriteModel));
      const plugin = writePlugins[0]?.plugin;
      if (!plugin) return;
      const inputDocumentBlock = plugin.blocks.find((block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT);
      if (!inputDocumentBlock) return;
      const params = openWormhole({ constructOnly: true, guidance, sessionId, considerationLength: 20000 });
      runDocumentPluginAction(inputDocumentBlock, plugin, documentPluginBridge, true, params, nonce, CrossAppSignal.WRITE_RUNNING);

      if (selectedWriteModel === "myVoice") {
        completeMyVoiceOnboarding();
      }
    });

    const handleTryUnlimitedClosed = useStableCallback(() => {
      userData?.update({ hideUnlimitedExperimentWriteTooltip: true });
      setShowTryUnlimitedTooltip(false);
    });

    const handleTryMuseClosed = useStableCallback(() => {
      userData?.update({ hideUnlimitedExperimentWriteTooltip: true });
      setShowTryMuseTooltip(false);
    });

    useEffect(() => {
      function handleClickOutside(event) {
        const target = event.target;

        if (ref.current && !ref.current.contains(target) && !modalOpen) {
          setToneOpen(false);
          setStoryToolOpen(false);
          setLabsOpen(false);
          setDescribeOpen(false);
          setWriteOpen(false);
        }
      }

      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("click", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("click", handleClickOutside);
      };
    }, [ref, modalOpen]);

    const openCustomizePluginsModal = () => {
      setIsCustomizePluginsModalOpen(true);
    };

    const [measureRef, { width }] = useMeasure({ polyfill: ResizeObserver });

    const wormholeButtonRef = useRef(null);

    const hasMemory = memory && memoryEnabled && memory.length > 0;

    useLayoutEffect(() => {
      if (!isSW2 || !hasDocument || orderedTools.length === 0) {
        return;
      }

      splitToolsIntoMainAndMore(orderedTools);
    }, [hasDocument, isSW2, orderedTools, width]);

    useLayoutEffect(() => {
      if (!isSW2 || !hasDocument || !menuInitialized || !mainMenuRef.current || orderedTools.length === 0) {
        return;
      }

      const row = mainMenuRef.current;
      const rowRect = row.getBoundingClientRect();
      const toolbarRect = getToolbarRect();
      const toolbarLeftGap = toolbarRect ? rowRect.left - toolbarRect.left : TOOLBAR_HORIZONTAL_MARGIN / 2;
      const maxToolbarRight = (isSW2
        ? (toolbarRect?.right ?? window.innerWidth) - toolbarLeftGap
        : rowRect.right) - (isSW2 ? MORE_TOOLS_OPEN_EXPANSION : 0);
      const moreToolsButton = row.querySelector('button[aria-label="More Tools"]');
      const moreToolsItem = moreToolsButton?.closest("li");
      const moreToolsRect = moreToolsItem?.getBoundingClientRect();
      if (!moreToolsRect) {
        return;
      }

      const minToolsToShow = isSW2 ? 0 : Math.min(MIN_NUMBER_OF_TOOLS_TO_SHOW, orderedTools.length);
      let projectedMoreToolsRight = moreToolsRect.right;
      let nextNumberOfToolsToShow = mainMenuTools.length;
      const visibleToolItems = Array.from(row.children).slice(0, mainMenuTools.length);

      while (
        projectedMoreToolsRight > maxToolbarRight + TOOLBAR_OVERFLOW_TOLERANCE &&
        nextNumberOfToolsToShow > minToolsToShow
      ) {
        nextNumberOfToolsToShow -= 1;
        const removedItem = visibleToolItems[nextNumberOfToolsToShow];
        projectedMoreToolsRight -= removedItem?.getBoundingClientRect().width || TOOLBAR_TOP_BUTTON_WIDTH;
      }

      if (nextNumberOfToolsToShow !== mainMenuTools.length) {
        setMainMenuTools(orderedTools.slice(0, nextNumberOfToolsToShow));
        setMoreMenuTools(orderedTools.slice(nextNumberOfToolsToShow));
        return;
      }

      sw2ToolbarHasSettledRef.current = true;
      setSw2ToolbarSettled(true);
    }, [hasDocument, isSW2, mainMenuTools.length, menuInitialized, orderedTools, width]);

    const writeHandler = () => {
      if (writeOpen) setWriteOpen(false);
      if (!guidanceOpen && writeMode !== WRITE_MODE_GUIDED) {
        const nonce = makeId(15);
        pluginOpenWormhole(undefined, `standard_write_${nonce}`);
      } else {
        openGuidance();
      }
    };

    const DefaultToolsMap = {
      write: {
        name: "Write",
        handler: writeHandler,
        topToolbarComponent: (
          <WriteButton
            disabled={!!trashedItem || installedPluginsLoading}
            showTryUnlimitedTooltip={showTryUnlimitedTooltip}
            showTryMuseTooltip={showTryMuseTooltip}
            onTryUnlimitedClosed={handleTryUnlimitedClosed}
            onTryMuseClosed={handleTryMuseClosed}
            defaultTooltipDisabled={guidanceOpen || writeOpen || (isTourActive && activeTourId === TourIds.WRITE_REWRITE)}
            wormholeButtonRef={wormholeButtonRef}
            writeOpen={writeOpen}
            setWriteOpen={setWriteOpen}
            guidanceOpen={guidanceOpen}
            writeMode={writeMode}
            pluginOpenWormhole={pluginOpenWormhole}
            openGuidance={openGuidance}
            hasMemory={hasMemory}
            tone={tone}
            memory={memory}
            setMemory={setMemory}
            memoryEnabled={memoryEnabled}
            setMemoryEnabled={setMemoryEnabled}
            tones={tones}
            editorOptions={editorOptions}
            wormholeOutputLength={wormholeOutputLength}
            wormholeOutputCards={wormholeOutputCards}
            creativitySliderPosition={creativitySliderPosition}
            setEditorOptions={setEditorOptions}
            setTone={setTone}
            openWormhole={openWormhole}
            writeModel={selectedWriteModel}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            setWriteMode={setWriteMode}
            myVoiceDisplayName={myVoiceDisplayName}
            showMyVoiceOnboarding={!!(editorOptions?.showMyVoiceWriteOnboarding || userData?.data?.showMyVoiceWriteOnboarding)}
            onMyVoiceOnboardingClosed={completeMyVoiceOnboarding}
            metadata={metadata}
            setMetadata={setMetadata}
          />
        ),
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            customContent={
              <>
                <strong>Place your cursor at the end of some text and click Write</strong>. Sudowrite will continue it! (The more text in your doc, the better.)
              </>
            }
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className="use-component-styles"
                onClick={writeHandler}
              >
                Write
              </button>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => hasDocument,
      },
      rewrite: {
        name: "Rewrite",
        handler: openFilter,
        topToolbarComponent: (
          <Item
            isMenuCollapsed={collapsed}
            shouldDisableTooltip={isTourActive && activeTourId === TourIds.WRITE_REWRITE}
            tip={
              <>
                <strong>Select a sentence, paragraph, or more then click Rewrite</strong> to rephrase, add description, mimic a famous style, or transform your text in other ways.
              </>
            }
            onClick={(e) => {
              e.preventDefault();
              if (isRewriteRunning) {
                return;
              }
              openFilter(e);
            }}
            dropdownLabel="Rewrite Options Menu"
            disabled={!!trashedItem || isRewriteRunning}
            dropdown={
              subscription.usesLedgers
                ? ({ close }) => <RewriteMenu onClose={close} />
                : null
            }
          >
            <StickyNoteAnchor anchorId={AnchorIds.TOP_TOOLBAR_REWRITE_BUTTON} />
            {isRewriteRunning ? (
              <Spinner size="md" />
            ) : (
              <SVG
                src="/assets/rewrite-icon.svg"
                height={22}
                width={22}
              />
            )}
            <p>Rewrite</p>
          </Item>
        ),
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            customContent={
              <>
                <strong>Select a sentence, paragraph, or more then click Rewrite</strong> to rephrase, add description, mimic a famous style, or transform your text in other ways.
              </>
            }
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className="use-component-styles"
                onClick={openFilter}
              >
                Rewrite
              </button>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => hasDocument,
      },
      describe: {
        name: "Describe",
        handler: openDescription,
        topToolbarComponent: (
          <Item
            isMenuCollapsed={collapsed}
            tip={
              <>
                <strong>Select a word or phrase and click Describe</strong> for suggestions using all five senses and even metaphors!
              </>
            }
            onClick={(e) => {
              e.preventDefault();
              if (isDescribeRunning) {
                return;
              }
              openDescription(e);
            }}
            dropdownLabel="Describe Options Menu"
            closeDropdownOnClick
            dropdown={
              <DescribeMenu
                setDescribeOpen={setDescribeOpen} //
                describeSenses={describeSenses}
                setDescribeSenses={setDescribeSenses}
              />
            }
            disabled={!!trashedItem || isDescribeRunning}
          >
            {isDescribeRunning ? <Spinner size="md" /> : <SVG src="/assets/describe-icon.svg" />}
            <p>Describe</p>
          </Item>
        ),
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            customContent={
              <>
                <strong>Select a word or phrase and click Describe</strong> for suggestions using all five senses and even metaphors!
              </>
            }
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className="use-component-styles"
                onClick={openDescription}
              >
                Describe
              </button>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => hasDocument,
      },
      feedback: {
        name: "Feedback",
        handler: handleFeedbackClickWithClose,
        topToolbarComponent: (
          <FeedbackTabLabel
            selectedCategory={selectedFeedbackCategory}
            isActive={chapterEditorActive}
            isLoading={isFeedbackRunning}
            isEstimating={isEstimatingFeedback}
            onClick={handleFeedbackClickWithClose}
            onChevronClick={handleFeedbackChevronClick}
            onStop={handleFeedbackStop}
            isMainActionDisabled={isFeedbackMainActionDisabled}
            showSelectedCategoryLabel={showFeedbackCategoryLabel}
          >
            <FeedbackTypeSelector
              isOpen={feedbackTypeSelectorOpen}
              selectedCategory={selectedFeedbackCategory}
              project={project}
              onClose={handleFeedbackTypeSelectorClose}
              onSelectCategory={handleFeedbackCategorySelect}
            />
            <FeedbackCreditWarning
              open={showCreditWarning}
              estimatedCredits={estimatedCredits}
              onCancel={dismissCreditWarning}
              onContinue={confirmFeedback}
            />
            <FeedbackPlanUpsellDialog
              open={feedbackPlanUpsellOpen}
              onOpenChange={setFeedbackPlanUpsellOpen}
              source={feedbackPlanUpsellSource}
              currentPlanTitle={getFeedbackUpsellCurrentPlanTitle()}
            />
          </FeedbackTabLabel>
        ),
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            customContent={
              <>
                <strong>Get AI feedback on your writing</strong> — developmental, line editing, and more.
              </>
            }
            triggerClassName="w-full"
          >
            <div className="feedback-more-menu-lockup">
              <button
                type="button"
                className="use-component-styles"
                disabled={isEstimatingFeedback || chapterEditorActive}
                onClick={handleFeedbackClickWithClose}
              >
                {isEstimatingFeedback && <Spinner size="md" />}
                {isEstimatingFeedback ? "Estimating Feedback…" : "Feedback"}
              </button>
              <span
                className="feedback-tier-pro-chip"
                aria-label="Pro feature"
              >
                <SVG
                  src="/assets/agentic-chat-sparkle.svg"
                  className="feedback-tier-pro-chip-icon"
                  aria-hidden="true"
                />
                <span className="feedback-tier-pro-chip-text">Pro</span>
              </span>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => hasDocument && isFeedbackBetaUser,
      },
      "old-feedback": {
        name: "Feedback",
        handler: openFeedback,
        topToolbarComponent: (
          <Item
            isMenuCollapsed={collapsed}
            tip={
              <>
                <strong>Select text and click Feedback.</strong> for "AI reader" opinions. Works best for fiction between 400-10,000 words.
              </>
            }
            onClick={openFeedback}
            disabled={!!trashedItem}
          >
            <SVG
              src="/assets/plugin.svg"
              height={22}
              width={20}
            />
            <p>Feedback</p>
          </Item>
        ),
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            customContent={
              <>
                <strong>Select text and click Feedback.</strong> for "AI reader" opinions. Works best for fiction between 400-10,000 words.
              </>
            }
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className="use-component-styles"
                onClick={openFeedback}
              >
                Feedback
              </button>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => false,
      },
      brainstorm: {
        name: "Brainstorm",
        handler: openBrainstorm,
        topToolbarComponent: (
          <Item
            isMenuCollapsed={collapsed}
            tip={
              <>
                <strong>Use Brainstorm for help with ideas</strong> for character names, dialogue, world details, descriptions, plot points, or anything else!
              </>
            }
            onClick={openBrainstorm}
            disabled={!!trashedItem}
          >
            <SVG
              src="/assets/brainstorm-icon.svg"
              height={22}
              width={20}
            />
            <p>Brainstorm</p>
          </Item>
        ),
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            customContent={
              <>
                <strong>Use Brainstorm for help with ideas</strong> for character names, dialogue, world details, descriptions, plot points, or anything else!
              </>
            }
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className="use-component-styles"
                onClick={openBrainstorm}
              >
                Brainstorm
              </button>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => hasDocument,
      },
      firstDraft: {
        name: "First Draft",
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            text={"Get a quick first draft from a simple prompt. Use in a totally blank document."}
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className={classnames("use-component-styles", { disabled: !showFirstDraft })}
                onClick={() => {
                  setCommandModeState("show");
                  setProseGeneratorActive(false); // turn off prose generator overlay if it's on
                }}
              >
                First Draft
              </button>
            </div>
          </SudoTooltip>
        ),
        topToolbarComponent: (
          <Item
            onClick={(e) => {
              e.preventDefault();
              setCommandModeState("show");
              setProseGeneratorActive(false);
            }}
            tip={
              <>
                <strong>Get a quick First Draft from a simple prompt.</strong> Use in a totally blank document.
              </>
            }
            disabled={!showFirstDraft}
          >
            <SVG src="/assets/plugin.svg" />
            <p>First Draft</p>
          </Item>
        ),
        shouldShow: () => hasDocument,
      },
      visualize: {
        name: "Visualize",
        handler: openVisualize,
        topToolbarComponent:
          hasAccessToFeatures && subscription.activeSubscription && subscription.usesQuota
            ? (() => {
                const disabledViz = highlightedWordCount < MIN_VISUALIZE_WORDS || highlightedWordCount > MAX_VISUALIZE_WORDS;
                return (
                  <Item
                    onClick={openVisualize}
                    disabled={disabledViz || !!trashedItem}
                    tip={
                      <>
                        <strong>Visualize your story with AI-generated images.</strong> works with between {MIN_VISUALIZE_WORDS} and {MAX_VISUALIZE_WORDS} words.
                      </>
                    }
                  >
                    <SVG src="/assets/Visualize.svg" />
                    <p>Visualize</p>
                  </Item>
                );
              })()
            : null,
        customMoreMenuComponent: (
          <SudoTooltip
            position="left"
            text={getVisualizedText}
            triggerClassName="w-full"
          >
            <div>
              <button
                type="button"
                className={classnames("use-component-styles", { visualize: true, disabled: highlightedWordCount < MIN_VISUALIZE_WORDS || highlightedWordCount > MAX_VISUALIZE_WORDS })}
                onClick={openVisualize}
              >
                Visualize
              </button>
            </div>
          </SudoTooltip>
        ),
        shouldShow: () => hasDocument,
      },
      canvas: {
        name: "Canvas",
        handler: () => history.push(`/app/project/${project.id}/canvas`),
        topToolbarComponent: (
          <Item onClick={() => history.push(`/app/project/${project.id}/canvas`)}>
            <SVG
              style={{ width: 20, height: 20, marginRight: 7, marginTop: -2 }}
              src="/assets/canvas.svg"
            />
            <p>Canvas</p>
          </Item>
        ),
        shouldShow: () => hasDocument,
      },
      reviser: {
        name: "Reviser",
        handler: () => history.push(`/app/project/${project.id}/reviser`),
        topToolbarComponent: subscription.hasAccessToReviser ? (
          <Item onClick={() => history.push(`/app/project/${project.id}/reviser`)}>
            <SVG src="/assets/plugin.svg" />
            <p>Reviser</p>
          </Item>
        ) : null,
        shouldShow: () => hasDocument && subscription.hasAccessToReviser,
      },
      admin: {
        name: "Admin",
        handler: openAdmin,
        topToolbarComponent: openAdmin ? (
          <Item onClick={openAdmin}>
            <SVG src="/assets/plugin.svg" />
            <p>Admin</p>
          </Item>
        ) : null,
        shouldShow: () => hasDocument && openAdmin,
      },
    };

    const sprint = useNovelNovemberSprint();
    const freeWrite = sprint.writeAvailable && selectedWriteModel === "excellent";
    const resolvedMainMenuTools = hasAccessToFeatures ? mainMenuTools : freeWrite ? ["write"] : [];
    const sw2ToolbarLoadingTools = [
      { label: "Write", icon: "/assets/write.svg" },
      { label: "Rewrite", icon: "/assets/rewrite-icon.svg" },
      { label: "Describe", icon: "/assets/describe-icon.svg" },
      { label: "Feedback", icon: "/assets/feedback-developmental.svg" },
      { label: "Brainstorm", icon: "/assets/brainstorm-icon.svg" },
      { label: "Visualize", icon: "/assets/Visualize.svg" },
      { label: "More Tools", icon: "/assets/plugin.svg" },
    ];

    if (minimal) {
      const portalTarget = menuRef?.current;
      if (!portalTarget) {
        return null;
      }
      return ReactDOM.createPortal(
        <TopMenuContainer
          isMenuCollapsed={collapsed}
          ref={ref}
        >
          <div
            className={classnames("m-inner", { collapsed })}
            ref={measureRef}
          >
            {!handledByTopBar && !hideBackButton && (!isTourActive || activeTourId === TourIds.FIND_TOUR_AGAIN) && (
              <Button
                as="a"
                href={(() => {
                  if (project) {
                    const storedProject = getProjectFromFirebaseId(project.id);
                    if (storedProject) {
                      const deleted = isInTrash(storedProject.id);
                      return deleted ? `/app/projects/deleted/${storedProject.folder_id || ""}` : `/app/folder/${storedProject.folder_id || "app"}`;
                    }
                  }
                  return "/app";
                })()}
                onClick={(e) => {
                  if (!e.metaKey && !e.ctrlKey && !e.button) {
                    e.preventDefault();
                    const href = e.currentTarget.getAttribute("href");
                    history.push(href);
                  }
                }}
                secondaryGrey={true}
                className="use-component-styles menu-back-button"
                aria-label="Back"
              >
                <SVG
                  className="icon-button"
                  src="/assets/back-arrow.svg"
                />
                {width > HIDE_BREAKPOINTS.back && <span>Back</span>}
              </Button>
            )}
            <div className="right">
              {!handledByTopBar && !hideSettings && userData && userData.fetched && subscriptionJSON && (
                <SettingsPanel
                  setOpen={setSettingsOpen}
                  open={settingsOpen}
                  demo={demo}
                  setDemo={(d) => {
                    setDemo(d);
                  }}
                  subscription={subscriptionJSON}
                  font={editorOptions.font}
                  fontSize={editorOptions.fontSize}
                  textIndent={editorOptions.textIndent}
                  lineHeight={editorOptions.lineHeight}
                  paragraphSpacing={editorOptions.paragraphSpacing}
                  showWordCount={editorOptions.showWordCount}
                  creativitySliderPosition={editorOptions.creativitySliderPosition}
                  theme={editorOptions.theme}
                  showCreditsUsedToast={editorOptions.showCreditsUsedToast}
                  onSubmit={(data) => {
                    setEditorOptions(update(editorOptions, { $merge: data }));
                  }}
                  v2={true}
                />
              )}

              {!handledByTopBar && !hideHelp && (
                <div className="toolbar-help-container">
                  <HelpMenu
                    setOpen={setHelpOpen}
                    open={helpOpen}
                    subscription={subscriptionJSON}
                    showFront={showFront}
                    openAgenticChatFromHelp={openAgenticChatFromHelp}
                    v2={true}
                  />
                </div>
              )}
            </div>
            <div className="clear"></div>
          </div>
        </TopMenuContainer>,
        portalTarget
      );
    }

    if (!hasDocument) {
      return null;
    }

    const portalTarget = menuRef?.current;
    if (!portalTarget) {
      return null;
    }

    return ReactDOM.createPortal(
      <TopMenuContainer
        isMenuCollapsed={collapsed}
        ref={ref}
      >
        <div
          className={classnames("m-inner", { collapsed, "handled-by-top-bar": handledByTopBar })}
          ref={measureRef}
        >
          {!handledByTopBar && !hideBackButton && (!isTourActive || activeTourId === TourIds.FIND_TOUR_AGAIN || isOnStepWithHiddenExitButton) && (
            <Button
              as="a"
              href={(() => {
                if (project) {
                  const storedProject = getProjectFromFirebaseId(project.id);
                  if (storedProject) {
                    const deleted = isInTrash(storedProject.id);
                    return deleted ? `/app/projects/deleted/${storedProject.folder_id || ""}` : `/app/folder/${storedProject.folder_id || "app"}`;
                  }
                }
                return "/app";
              })()}
              onClick={(e) => {
                // Only handle regular clicks
                if (!e.metaKey && !e.ctrlKey && !e.button) {
                  e.preventDefault();
                  const href = e.currentTarget.getAttribute("href");
                  history.push(href);
                }
              }}
              secondaryGrey={true}
              className="use-component-styles back-button"
              aria-label="Back"
            >
              <SVG
                className="icon-button"
                src="/assets/back-arrow.svg"
              />
              {width > HIDE_BREAKPOINTS.back && <span>Back</span>}
            </Button>
          )}
          {shouldShowLegacyExitTourButton && <div className="back-button-tour-spacer" />}
          {shouldShowLegacyExitTourButton && (
            <div className="right-tour">
              <ExitTourButton />
            </div>
          )}
          {!handledByTopBar && !shouldShowLegacyExitTourButton && (
            <div className="right">
              {!hideSettings && userData && userData.fetched && subscriptionJSON && (
                <SettingsPanel
                  setOpen={setSettingsOpen}
                  open={settingsOpen}
                  demo={demo}
                  setDemo={(d) => {
                    setDemo(d);
                  }}
                  subscription={subscriptionJSON}
                  font={editorOptions.font}
                  fontSize={editorOptions.fontSize}
                  textIndent={editorOptions.textIndent}
                  lineHeight={editorOptions.lineHeight}
                  paragraphSpacing={editorOptions.paragraphSpacing}
                  showWordCount={editorOptions.showWordCount}
                  creativitySliderPosition={editorOptions.creativitySliderPosition}
                  theme={editorOptions.theme}
                  showCreditsUsedToast={editorOptions.showCreditsUsedToast}
                  onSubmit={(data) => {
                    setEditorOptions(update(editorOptions, { $merge: data }));
                  }}
                  v2={true}
                />
              )}

              {!hideHelp && (
                <div className="toolbar-help-container">
                  <HelpMenu
                    setOpen={setHelpOpen}
                    open={helpOpen}
                    subscription={subscriptionJSON}
                    showFront={showFront}
                    openAgenticChatFromHelp={openAgenticChatFromHelp}
                    v2={true}
                  />
                </div>
              )}

              {hasDocument && (
                <div className="toolbar-documentsharing">
                  <DocumentSharingMenu documentId={documentId} />
                </div>
              )}

              <div className="membership-status">
                <div className="message">
                  <span>
                    {statusText && <div className="status-text">{statusText}</div>}

                    {!hideHiring && (
                      <a
                        className="pill-link"
                        href="https://bit.ly/3ZO6mpj"
                        target="_blank"
                      >
                        🚀 We're hiring!
                      </a>
                    )}

                    {!hideNew && <FeaturebaseChangelog />}
                  </span>
                </div>
              </div>
            </div>
          )}
          {!handledByTopBar && !shouldShowLegacyExitTourButton && (
            <div className="status">
              <Status
                wordCount={wordCount}
                highlightedWordCount={highlightedWordCount}
                editorOptions={editorOptions}
                saved={saved}
              />
            </div>
          )}

          {/*
        use display none technique to show and hide the guidance
        which avoids a weird bug in Safari where calling focus() on the custom
        text field inside Guidances causes multiverse container to shift
        upwards. this is due to some bug that only occurs when mounting and unmounting the
        guidance component.

        so instead, we always have the component mounted and show/hide as necessary
         */}
          <div style={{ display: guidanceOpen ? "block" : "none" }}>
            <Guidances
              wormholeButtonRef={wormholeButtonRef}
              open={guidanceOpen}
              setAutomaticGuidances={setAutomaticGuidances}
              loading={loadingGuidances}
              fetchGuidance={fetchGuidance}
              guidances={guidances}
              openWormhole={openWormhole}
              pluginOpenWormhole={pluginOpenWormhole}
              closeGuidance={closeGuidance}
              automaticGuidances={automaticGuidances}
              setGuidances={setGuidances}
              writeModel={selectedWriteModel}
            />
          </div>

          {isSW2 && !sw2ToolbarSettled && (
            <div
              className="sw2-menu-loading-placeholder"
              aria-hidden="true"
            >
              {sw2ToolbarLoadingTools.map(({ label, icon }) => (
                <span
                  className="sw2-menu-loading-tool"
                  key={label}
                >
                  <img
                    src={icon}
                    alt=""
                  />
                  <span>{label}</span>
                </span>
              ))}
            </div>
          )}
          <ul
            className="main"
            ref={mainMenuRef}
            style={isSW2 && !sw2ToolbarSettled ? { visibility: "hidden", position: "absolute", pointerEvents: "none" } : undefined}
          >
            {(hasAccessToFeatures || freeWrite) && menuInitialized &&
              resolvedMainMenuTools.map((toolId) => {
                // either it's a default tool
                const tool = DefaultToolsMap[toolId];
                if (tool) {
                  return (
                    <React.Fragment key={toolId}>
                      {tool.topToolbarComponent}
                    </React.Fragment>
                  );
                }
                // or it's a custom plugin
                const plugin = pluginSchemasMap[toolId];
                const handler = makePluginHandler(toolId, pluginSchemasMap);
                const pluginSignal = getPluginSignal(toolId);
                const isPluginRunning = crossAppSignals[pluginSignal] || false;

                if (!plugin) return null;
                const shouldAnchorPlugin = isGeneratedPlugin(plugin.plugin);
                return (
                  <React.Fragment key={toolId}>
                    <Item
                      isMenuCollapsed={collapsed}
                      maxWidth={TOP_BUTTON_WIDTH}
                      disabled={isPluginRunning}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isPluginRunning) return;
                        handler(e.currentTarget);
                      }}
                    >
                      {shouldAnchorPlugin && <StickyNoteAnchor anchorId={AnchorIds.CUSTOM_PLUGIN_BUTTON} />}
                      {isPluginRunning ? <Spinner size="md" /> : <SVG src="/assets/plugin.svg" />}
                      <p>{plugin.plugin.name}</p>
                    </Item>
                  </React.Fragment>
                );
              })}

            {!hasAccessToFeatures && !freeWrite && (
              <>
                <Item
                  isMenuCollapsed={collapsed}
                  disabled={true}
                >
                  <SVG src="/assets/write.svg" />
                  <p>Write</p>
                </Item>

                <Item
                  isMenuCollapsed={collapsed}
                  disabled={true}
                >
                  <SVG
                    src="/assets/rewrite-icon.svg"
                    height={22}
                    width={22}
                  />
                  <p>Rewrite</p>
                </Item>

                <Item
                  isMenuCollapsed={collapsed}
                  disabled={true}
                >
                  <SVG src="/assets/describe-icon.svg" />
                  <p>Describe</p>
                </Item>

                {isFeedbackBetaUser && (
                  <FeedbackTabLabel
                    selectedCategory={selectedFeedbackCategory}
                    isActive={chapterEditorActive}
                    isLoading={isFeedbackRunning}
                    isEstimating={isEstimatingFeedback}
                    onClick={handleFeedbackClickWithClose}
                    onChevronClick={handleFeedbackChevronClick}
                    onStop={handleFeedbackStop}
                    isMainActionDisabled={isFeedbackMainActionDisabled}
                    showSelectedCategoryLabel={showFeedbackCategoryLabel}
                  >
                    <FeedbackTypeSelector
                      isOpen={feedbackTypeSelectorOpen}
                      selectedCategory={selectedFeedbackCategory}
                      project={project}
                      onClose={handleFeedbackTypeSelectorClose}
                      onSelectCategory={handleFeedbackCategorySelect}
                    />
                    <FeedbackCreditWarning
                      open={showCreditWarning}
                      estimatedCredits={estimatedCredits}
                      onCancel={dismissCreditWarning}
                      onContinue={confirmFeedback}
                    />
                    <FeedbackPlanUpsellDialog
                      open={feedbackPlanUpsellOpen}
                      onOpenChange={setFeedbackPlanUpsellOpen}
                      source={feedbackPlanUpsellSource}
                      currentPlanTitle={getFeedbackUpsellCurrentPlanTitle()}
                    />
                  </FeedbackTabLabel>
                )}



                <Item
                  isMenuCollapsed={collapsed}
                  disabled={true}
                >
                  <SVG
                    src="/assets/brainstorm-icon.svg"
                    height={22}
                    width={20}
                  />
                  <p>Brainstorm</p>
                </Item>
              </>
            )}

            {menuInitialized && (
              <MoreMenu
                MoreMenuDefaultItemsMap={DefaultToolsMap}
                pluginSchemasMap={pluginSchemasMap}
                makePluginHandler={makePluginHandler}
                labsOpen={labsOpen}
                setLabsOpen={setLabsOpen}
                setToneOpen={setToneOpen}
                setStoryToolOpen={setStoryToolOpen}
                setDescribeOpen={setDescribeOpen}
                setWriteOpen={setWriteOpen}
                userData={userData}
                subscription={subscription}
                width={width}
                hideBreakpointLabs={HIDE_BREAKPOINTS.labs}
                labItems={moreMenuTools}
                triggerProps={pluginPopover.triggerProps}
                trashedItem={trashedItem}
                openCustomizePluginsModal={openCustomizePluginsModal}
                disabled={!hasAccessToFeatures}
              />
            )}
          </ul>

          {!hasAccessToFeatures && !subscription.activeSubscription() && !subscription.usesQuota && (
            <div className="subscribe">
              Your trial has expired: <SubscribeButton origin="editor_trial_expired">Start Membership</SubscribeButton>
            </div>
          )}

          <div className="clear"></div>
        </div>
        {isCustomizePluginsModalOpen && (
          <CustomizePlugins
            isOpen
            installations={orderedTools.map((toolId) => {
              const plugin = pluginSchemasMap[toolId];
              if (!plugin) return { plugin: { id: toolId, name: DefaultToolsMap[toolId]?.name }, id: toolId, builtIn: true };
              return plugin;
            })}
            pluginSchemasMap={pluginSchemasMap}
            onDone={handleCustomizePluginsDone}
            setOrderedTools={setOrderedTools}
            mainMenuCount={mainMenuTools.length}
          />
        )}
        {!labsOpen && pluginPopover.element}
      </TopMenuContainer>,
      portalTarget
    );
  }
);

export default Menu;
